import { EditorContent, useEditor } from '@tiptap/react';
import { baseEditorClasses, codeEditorClasses, lowlight } from './editorConfig';
import { CustomMention, getMentionConfig } from './extensions/CustomMention';
import { createMentionSuggestion } from './mentionSuggestion';
import { S3Image } from './extensions/S3Image';
import EditorBubbleMenu from './EditorBubbleMenu';
import StarterKit from '@tiptap/starter-kit';
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import Gapcursor from '@tiptap/extension-gapcursor';
import { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/context/AuthContext';

// Syntax highlighting
import '../../syntax-highlighting.css';
import Link from '@tiptap/extension-link';
import { S3File } from './extensions/S3File';

const TiptapEditor = forwardRef(function TiptapEditor({
    // Basic configuration
    content = '',
    placeholder = '',
    editable = true,
    className = '',

    // Editor features
    withCodeBlocks = true,
    withMentions = true,
    withS3Images = true,
    withS3Uploads = false,
    withBubbleMenu = true,

    // Handlers
    onUpdate = () => { },
    onKeyDown,
    onEditorReady,

    organizationSlug,
    taskKey,
    onTaskChange,
    onFileUpload,

    // Custom styling
    customEditorClasses,
}, ref) {
    const { axiosInstance } = useAuth();
    const [isUploading, setIsUploading] = useState(false);
    const { toast } = useToast();

    const handleFileUpload = async (file, insertIntoEditor = false, position = null) => {
        // Only proceed if withS3Uploads is enabled
        if (!withS3Uploads) {
            toast({
                description: "File uploads are not enabled",
                variant: "destructive"
            });
            return;
        }

        setIsUploading(true);

        try {
            let placeholderUrl;
            if (insertIntoEditor) {
                const uploadId = Math.random().toString(36).substring(7);
                placeholderUrl = URL.createObjectURL(file);
                // Insert with upload ID in attrs
                if (position !== null) {
                    if (file.type.startsWith('image/')) {
                        editor.chain().focus().insertContentAt(position, {
                            type: 's3Image',
                            attrs: {
                                src: placeholderUrl,
                                key: null,
                                uploadId,
                            }
                        }).run();
                    } else {
                        editor.chain().focus().insertContentAt(position, {
                            type: 's3File',
                            attrs: {
                                src: placeholderUrl,
                                key: null,
                                filename: file.name,
                                contentType: file.type,
                                fileSize: file.size,
                                uploadId,
                            }
                        }).run();
                    }
                } else {
                    if (file.type.startsWith('image/')) {
                        editor.chain().focus().setS3Image({
                            src: placeholderUrl,
                            key: null,
                            uploadId,
                        }).run();
                    } else {
                        editor.chain().focus().setS3File({
                            src: placeholderUrl,
                            key: null,
                            filename: file.name,
                            contentType: file.type,
                            fileSize: file.size,
                            uploadId,
                        }).run();
                    }
                }
            }

            const formData = new FormData();
            formData.append('file', file);

            let response;
            if (taskKey && organizationSlug) {
                // Existing task upload
                response = await axiosInstance.post(
                    `/tasks/${organizationSlug}/${taskKey}/attachments`,
                    formData
                );
            } else {
                response = await onFileUpload(file);
            }

            if (insertIntoEditor) {
                const { url, filename, key } = response.data.attachments[response.data.attachments.length - 1];

                const { state } = editor;
                const { doc } = state;
                doc.descendants((node, pos) => {
                    if (file.type.startsWith('image/')) {
                        if (node.type.name === 's3Image' && node.attrs.src === placeholderUrl) {
                            editor.chain()
                                .setNodeSelection(pos)
                                .updateS3Image({
                                    src: url,
                                    key: key,
                                    alt: filename,
                                    uploadId: null,
                                })
                                .run();
                            URL.revokeObjectURL(placeholderUrl);
                        }
                    } else {
                        if (node.type.name === 's3File' && node.attrs.src === placeholderUrl) {
                            editor.chain()
                                .setNodeSelection(pos)
                                .updateS3File({
                                    src: url,
                                    key: key,
                                    filename: filename,
                                    uploadId: null,
                                })
                                .run();
                            URL.revokeObjectURL(placeholderUrl);
                        }
                    }
                });
            }

            if (onTaskChange) {
                onTaskChange(response.data);
            }

            if (onUpdate) {
                onUpdate({ editor });
            }

            toast({
                description: "File uploaded successfully"
            });
        } catch (error) {
            console.error(error);
            toast({
                description: "Failed to upload file",
                variant: "destructive"
            });
        } finally {
            setIsUploading(false);
        }
    };

    const editor = useEditor({
        extensions: [
            Link.extend({ inclusive: false }).configure({
                defaultProtocol: 'https',
                HTMLAttributes: {
                    class: 'text-violet-600 dark:text-violet-400 cursor-pointer',
                },
                openOnClick: false,
            }),
            StarterKit.configure({
                codeBlock: !withCodeBlocks,
            }),
            withCodeBlocks && CodeBlockLowlight.configure({
                lowlight,
                HTMLAttributes: {
                    class: 'hljs'
                },
            }),
            Underline,
            TextAlign.configure({
                types: ['heading', 'paragraph', 's3Image'],
            }),
            Placeholder.configure({
                placeholder,
                emptyEditorClass: "before:content-[attr(data-placeholder)] before:float-left before:text-neutral-500 dark:before:text-neutral-600 before:h-0 before:pointer-events-none",
            }),
            withMentions && CustomMention.configure({
                ...getMentionConfig(axiosInstance, organizationSlug),
                suggestion: withMentions && axiosInstance && organizationSlug ?
                    createMentionSuggestion(axiosInstance, organizationSlug) :
                    undefined,
            }),
            withS3Images && S3Image,
            withS3Images && S3File,
            Gapcursor,
        ].filter(Boolean),

        editorProps: {
            attributes: {
                class: customEditorClasses || `${baseEditorClasses} ${codeEditorClasses} ${className}`,
            },
            handlePaste: (view, event) => {
                if (!withS3Uploads) return false;

                const items = Array.from(event.clipboardData?.items || []);
                const fileItem = items.find(item => item.type.indexOf('/') !== -1);

                if (fileItem) {
                    event.preventDefault();
                    const file = fileItem.getAsFile();
                    if (file) {
                        setIsUploading(true);
                        handleFileUpload(file, true);
                    }
                }
                return false;
            },
            handleDrop: (view, event, slice, moved) => {
                if (!withS3Uploads) return false;

                if (!moved && event.dataTransfer?.files?.length) {
                    const file = event.dataTransfer.files[0];
                    setIsUploading(true);
                    event.preventDefault();
                    const coordinates = view.posAtCoords({
                        left: event.clientX,
                        top: event.clientY,
                    });

                    if (coordinates) {
                        handleFileUpload(file, true, coordinates.pos);
                    }
                    return true;
                }
                return false;
            },
            handleKeyDown: onKeyDown,
        },

        content,
        editable: !isUploading && editable,
        onUpdate: ({ editor }) => {
            // Check if any nodes are currently uploading
            let hasUploadingNodes = false;
            editor.state.doc.descendants(node => {
                if (node.type.name === 's3Image' && node.attrs.uploadId) {
                    hasUploadingNodes = true;
                }
            });

            console.log('onUpdate, hasUploadingNodes:', hasUploadingNodes);
            if (!hasUploadingNodes) {
                onUpdate({ editor });
            }
        },
    });

    useEffect(() => {
        if (editor && onEditorReady) {
            onEditorReady(editor);
        }
    }, [editor, onEditorReady]);

    // Add this useImperativeHandle hook after the editor definition
    useImperativeHandle(ref, () => ({
        commands: editor?.commands,
        editor: editor
    }), [editor]);

    if (!editor) {
        return null;
    }

    return (
        <div className="relative">
            {withBubbleMenu && <EditorBubbleMenu editor={editor}/>}
            <EditorContent editor={editor} />
        </div>
    );
});

TiptapEditor.displayName = 'TiptapEditor';

export default TiptapEditor;
